import { useState, useEffect, useCallback } from 'react';
import { route } from '../utils/route';
import { dispatchCartCount } from '../utils/cartBadge';
import type { CartItem, CartPackTracking, ProductPack } from '../types/packs';

const STORAGE_KEY = 'cart_packs';

export function useCartPacks() {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [packTracking, setPackTracking] = useState<CartPackTracking>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) setPackTracking(JSON.parse(stored));
    } catch {}
  }, []);

  const saveTracking = useCallback((tracking: CartPackTracking) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(tracking)); } catch {}
  }, []);

  const refreshCart = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(route('api.cart.summary'), { credentials: 'include', cache: 'no-store' });
      const data = await response.json();
      if (data.content && Array.isArray(data.content)) {
        setCartItems(data.content.map((item: any) => ({
          id: item.id,
          format_id: item.format_id,
          quantity: item.quantity,
        })));
        dispatchCartCount(data.content.length);
      }
    } catch {} finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { refreshCart(); }, [refreshCart]);

  const isPackInCart = useCallback((pack: ProductPack): boolean => {
    const cartKeys = new Set(cartItems.map(i => `${i.id}:${i.format_id}`));
    return pack.items.every(item =>
      cartKeys.has(`${item.product_id}:${item.product_format_id}`)
    );
  }, [cartItems]);

  const addPackTracking = useCallback((pack: ProductPack, itemKeys?: string[]) => {
    const next: CartPackTracking = {
      ...packTracking,
      [pack.id]: {
        addedAt: new Date().toISOString(),
        title: pack.title,
        itemKeys: itemKeys ?? pack.items.map(i => `${i.product_id}:${i.product_format_id}`),
      },
    };
    setPackTracking(next);
    saveTracking(next);
  }, [packTracking, saveTracking]);

  const removePackTracking = useCallback((packId: number) => {
    const next = { ...packTracking };
    delete next[packId];
    setPackTracking(next);
    saveTracking(next);
  }, [packTracking, saveTracking]);

  return { cartItems, packTracking, isPackInCart, addPackTracking, removePackTracking, refreshCart, isLoading };
}
